import {
  SessionTaskAttemptResultEnum,
  SessionTaskAttemptStateEnum,
} from "~/lib/objects";

/**
 * Type for problem input - requires problemId and problemOrder
 */
type ProblemInput = {
  problemId: number;
  problemOrder: number;
};

export type ProblemSetTargetInput = ProblemInput & {
  result: SessionTaskAttemptResultEnum;
  state: SessionTaskAttemptStateEnum;
};

export function findProblemSetTargetProblem(
  problems: ProblemSetTargetInput[],
): ProblemInput | null {
  return findFirstTargetProblem(
    problems,
    problems.map((problem) => ({
      problemId: problem.problemId,
      finalResult: problem.result,
      finalState: problem.state,
    })),
  );
}

/**
 * Type for attempt input - requires problemId, finalResult and finalState
 */
type AttemptInput = {
  problemId: number;
  finalResult: SessionTaskAttemptResultEnum;
  finalState: SessionTaskAttemptStateEnum;
};

/**
 * Determines if a problem is a "target" problem (unsolved or erroneous).
 * A problem is considered a target if:
 * - It has no attempt (not started)
 * - Its finalResult is PENDING or LOSS
 * - Its finalState is HAS_MISTAKES or IN_PROGRESS
 */
function isTargetProblem(
  problem: ProblemInput,
  attempts: AttemptInput[],
): boolean {
  const attempt = attempts.find((a) => a.problemId === problem.problemId);

  // No attempt means problem is not started (PENDING)
  if (!attempt) {
    return true;
  }

  // Check if result is PENDING or LOSS
  if (
    attempt.finalResult === SessionTaskAttemptResultEnum.PENDING ||
    attempt.finalResult === SessionTaskAttemptResultEnum.LOSS
  ) {
    return true;
  }

  // Check if state is HAS_MISTAKES or IN_PROGRESS
  if (
    attempt.finalState === SessionTaskAttemptStateEnum.HAS_MISTAKES ||
    attempt.finalState === SessionTaskAttemptStateEnum.IN_PROGRESS
  ) {
    return true;
  }

  return false;
}

/**
 * Finds the first target problem (unsolved or erroneous) in the list.
 *
 * @param problems - Array of problems with problemId and problemOrder
 * @param attempts - Array of attempts with problemId, finalResult and finalState
 * @param startIndex - Optional starting index for relative search (searches after this index first, then wraps around)
 * @returns The first target problem found, or the first problem if all are solved, or null if problems array is empty
 */
export function findFirstTargetProblem(
  problems: ProblemInput[],
  attempts: AttemptInput[],
  startIndex?: number,
): ProblemInput | null {
  // Return null for empty problems array
  if (!problems || problems.length === 0) {
    return null;
  }

  // If startIndex is provided and valid, search from that position
  if (
    startIndex !== undefined &&
    startIndex >= 0 &&
    startIndex < problems.length
  ) {
    // Search from startIndex + 1 to end
    for (let i = startIndex + 1; i < problems.length; i++) {
      if (isTargetProblem(problems[i]!, attempts)) {
        return problems[i]!;
      }
    }

    // Wrap around: search from 0 to startIndex (inclusive)
    for (let i = 0; i <= startIndex; i++) {
      if (isTargetProblem(problems[i]!, attempts)) {
        return problems[i]!;
      }
    }

    // No target found, return first problem
    return problems[0]!;
  }

  // No startIndex: search from beginning
  for (const problem of problems) {
    if (isTargetProblem(problem, attempts)) {
      return problem;
    }
  }

  // No target found, return first problem
  return problems[0]!;
}
